import { useState, useCallback, useEffect } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useEditVehicleStock } from "hooks/stockHook"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { useToastMessage } from "hooks/useToastMessage"
import { useVehicleForm } from "hooks/useVehicleForm"
import FormikCreatableField from "components/Common/FormikCreatableField"

export default function StockIdentificationUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  const { handleResponse } = useToastMessage()

  // Model ID (needed for filtering chassis & engine codes)
  const vs_model_id = stock?.vs_model_id

  const validationSchema = Yup.object({
    chassis_number: Yup.string().nullable(),
    engine_number: Yup.string().nullable(),
    full_chassis: Yup.string().nullable(),
    brta_reg_no: Yup.string().nullable(),
    vs_chassis_code_id: Yup.string().nullable(),
    vs_engine_code_id: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const res = await editStock({ id: stock?.id, ...values })
        handleResponse(
          res,
          "Identification details updated successfully.",
          true
        )
        if (res.success) {
          toggle()
          resetForm()
        }
      } catch (error) {
        console.error("Failed to update identification details:", error)
      }
    },
    [editStock, stock, handleResponse]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      chassis_number: stock?.chassis_number || "",
      engine_number: stock?.engine_number || "",
      full_chassis: stock?.full_chassis || "",
      brta_reg_no: stock?.brta_reg_no || "",
      vs_chassis_code_id: stock?.vs_chassis_code_id || "",
      vs_engine_code_id: stock?.vs_engine_code_id || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const { handleParams, optionLoading, engineCodesData, chassisCodesData } =
    useVehicleForm({ stockResponse: stock, isEdit: true, formik })

  // ✅ Set initial model id only if it exists
  const modelSlug = stock?.model?.slug || ""

  useEffect(() => {
    if (modelSlug) {
      handleParams("model", modelSlug)
    }
  }, [modelSlug, handleParams])

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-identification"
        btnName="Edit Identification"
      />

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Edit Identification Details</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              formik.handleSubmit()
            }}
          >
            <Row>
              {/* Chassis Code */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_chassis_code_id"
                  label="Chassis Code"
                  formik={formik}
                  data={chassisCodesData}
                  onChange={formik.handleChange}
                  placeholder="Select Code"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_chassis_code_id &&
                  formik.errors.vs_chassis_code_id && (
                    <FormFeedback>
                      {formik.errors.vs_chassis_code_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Chassis Number */}
              <Col md={6} className="mb-3">
                <Label>Chassis Number</Label>
                <Input
                  name="chassis_number"
                  value={formik.values.chassis_number}
                  onChange={formik.handleChange}
                  invalid={
                    formik.touched.chassis_number &&
                    !!formik.errors.chassis_number
                  }
                />
                <FormFeedback>{formik.errors.chassis_number}</FormFeedback>
              </Col>

              {/* Engine Code */}
              <Col md={6}>
                <FormikCreatableField
                  name="vs_engine_code_id"
                  label="Engine Code"
                  formik={formik}
                  data={engineCodesData}
                  onChange={formik.handleChange}
                  placeholder="Select Code"
                  isLoading={optionLoading}
                />
                {formik.touched.vs_engine_code_id &&
                  formik.errors.vs_engine_code_id && (
                    <FormFeedback>
                      {formik.errors.vs_engine_code_id}
                    </FormFeedback>
                  )}
              </Col>

              {/* Engine Number */}
              <Col md={6} className="mb-3">
                <Label>Engine Number</Label>
                <Input
                  name="engine_number"
                  value={formik.values.engine_number}
                  onChange={formik.handleChange}
                  invalid={
                    formik.touched.engine_number &&
                    !!formik.errors.engine_number
                  }
                />
                <FormFeedback>{formik.errors.engine_number}</FormFeedback>
              </Col>

              {/* BRTA Registration No */}
              <Col md={12} className="mb-3">
                <Label>BRTA Reg No</Label>
                <Input
                  name="brta_reg_no"
                  value={formik.values.brta_reg_no}
                  onChange={formik.handleChange}
                  invalid={
                    formik.touched.brta_reg_no && !!formik.errors.brta_reg_no
                  }
                />
                <FormFeedback>{formik.errors.brta_reg_no}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button type="submit" color="success" disabled={stockEditLoading}>
                Save Changes
                {stockEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
